/**
 * Module-scoped, per-usecase stash of the wizard Data Collection state for
 * the times the create-task wizard unmounts and comes back within the same
 * page session:
 *
 * - the "Add credentials" round-trip to Account Settings;
 * - the top-left Back (exit to Projects) followed by "Finish Task Creation"
 *   on the draft card.
 *
 * Unmounting the wizard destroys the parent-held `stateCacheRef` snapshot —
 * including locally attached files that are only uploaded on Next, and the
 * column / entity mapping of an upload that is still in flight (the server
 * only learns the mapping from `complete-dataset-upload`, after the signed
 * PUT lands). Stashing the snapshot lets `useDataCollectionHydration`
 * restore the step exactly, before falling back to server hydration.
 *
 * Keyed by usecase id and consumed at most once per mount, so a stash cannot
 * leak into another task. A page refresh drops it (module memory), which is
 * right: the detached upload chain does not survive a refresh either, and
 * the server docs then carry everything that landed.
 */

const stashes = new Map();

/**
 * @param {string} key - Usecase id the snapshot belongs to.
 * @param {Object} snapshot - `stateCacheRef.current`-shaped step snapshot
 *   (`null` clears the entry).
 */
export function stashDataCollectionState(key, snapshot) {
    if (!key) return;
    if (snapshot) stashes.set(key, snapshot);
    else stashes.delete(key);
}

/** Read the stashed snapshot for `key` without consuming it (`null` when none). */
export function peekDataCollectionStash(key) {
    return (key && stashes.get(key)) || null;
}

/**
 * Return and clear the stashed snapshot for `key`; `null` when none. Other
 * usecases' stashes are left alone.
 *
 * @param {string} key - Usecase id of the mounting Data Collection step.
 * @returns {Object|null}
 */
export function consumeDataCollectionStash(key) {
    const snapshot = peekDataCollectionStash(key);
    if (key) stashes.delete(key);
    return snapshot;
}

/** Forget the stash for `key` (wizard finished — training started). */
export function clearDataCollectionStash(key) {
    if (key) stashes.delete(key);
}
